import type {
  AssistantDirectory,
  AssistantTask,
  ConversationPlacement,
  CreateAssistantInput,
  CrowdexAssistant,
  MoveConversationInput,
  UpdateAssistantInput,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';

/**
 * The HTTP side of assistants (`/api/assistants`,
 * `/api/conversations/:id/placement`, docs/adr-assistants.md): no workspace is sent or read. The
 * directory store calls it through this interface so previews and tests can swap it.
 */
export interface WorkspaceDirectoryApi {
  list(): Promise<WorkspaceDirectorySnapshot>;
  createAssistant(input: CreateAssistantInput): Promise<CrowdexAssistant>;
  updateAssistant(id: string, input: UpdateAssistantInput): Promise<CrowdexAssistant>;
  moveConversation(
    conversationId: string,
    input: MoveConversationInput,
  ): Promise<ConversationPlacement>;
  assistantTasks(projectId: string): Promise<AssistantTask[]>;
  /** The assistant's channel, made the first time it is opened. */
  openProjectConversation(projectId: string): Promise<string>;
}

/** Everything the sidebar and the pickers read: one request at start, then events. */
export interface WorkspaceDirectorySnapshot {
  /** The user's assistants, the home one first. */
  projects: CrowdexAssistant[];
  /** Placement of every conversation that has one, by conversation id. */
  placements: Record<string, ConversationPlacement>;
}

const json = (body: unknown) => ({
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

const assistantPath = (id: string, rest = '') => `/api/assistants/${encodeURIComponent(id)}${rest}`;

/** The snapshot of what `GET /api/assistants` answers. */
export function directorySnapshot(directory: AssistantDirectory): WorkspaceDirectorySnapshot {
  return {
    projects: directory.assistants,
    placements: directory.placements,
  };
}

export const httpWorkspaceDirectoryApi: WorkspaceDirectoryApi = {
  list: async () => directorySnapshot(await apiFetch<AssistantDirectory>('/api/assistants')),
  createAssistant: (input) =>
    apiFetch<CrowdexAssistant>('/api/assistants', { method: 'POST', ...json(input) }),
  updateAssistant: (id, input) =>
    apiFetch<CrowdexAssistant>(assistantPath(id), { method: 'PATCH', ...json(input) }),
  moveConversation: (conversationId, input) =>
    apiFetch<ConversationPlacement>(
      `/api/conversations/${encodeURIComponent(conversationId)}/placement`,
      { method: 'PUT', ...json(input) },
    ),
  assistantTasks: async (projectId) =>
    (await apiFetch<{ tasks: AssistantTask[] }>(assistantPath(projectId, '/tasks'))).tasks,
  openProjectConversation: async (projectId) =>
    (
      await apiFetch<{ conversationId: string }>(assistantPath(projectId, '/conversation'), {
        method: 'POST',
      })
    ).conversationId,
};
